<script lang="ts">
  import { setContext } from "svelte"
  import Layout from "$app/Layouts/Admin.svelte"
  import SortField from "$app/components/SortField.svelte"
  import Modal from "$app/components/Modal.svelte"
  import Form from "./Form.svelte"
  import Book from "./Book.svelte"

  import { Pen, Trash, X, Check } from "lucide-svelte"
  import { formatDate, wait, xhr, toast, bullets } from "$app/utils"

  let props = $props()
  let books: BookType[] = $state(props.books)
  let categories: string[] = props.categories
  let values: string[] = props.values || []
  let sort_direction: "asc" | "desc" = $state(props.sort_direction || "asc")
  let sort_field = $state(props.title || "title")
  let statuses: string[] = props.statuses || []
  let limit: number = props.limit || 10

  let page = 1

  setContext("categories", categories)
  setContext("values", values)
  setContext("statuses", statuses)

  let currentBook: BookType = $state({}) as BookType
  let status = $state("")
  let openFormModal = $state(false)

  function editBook(book: BookType): void {
    currentBook = book
    status = "edit"
    openFormModal = true
  }

  async function sort(field: string): Promise<void> {
    if (sort_field === field) {
      sort_direction = sort_direction === "asc" ? "desc" : "asc"
    } else {
      sort_direction = "asc"
      sort_field = field
    }
    search(1)
  }

  function updateBook(book: BookType): void {
    status = ""
    const idx = books.findIndex((b) => b.id === book.id)
    books[idx] = book
  }

  function addBook(book: BookType): void {
    status = ""
    books = [book, ...books]
  }

  async function deleteBook(book: BookType): Promise<void> {
    book._selected = true
    books = books
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected book?")) {
      book._selected = false
      books = books
      return
    }

    try {
      await xhr.delete(`/admin/books/${book.id}`)
      books = books.filter((b) => b.id !== book.id)
      toast("Book deleted successfully", "success")
    } catch (e) {
      toast("Error deleting book", "error")
    } finally {
      book._selected = false
      books = books
    }
  }

  let searchText = $state("")
  let searchStatus = $state("")
  let searching = $state(false)

  async function search(page: number): Promise<void> {
    searching = true
    try {
      const resp = await xhr.get("/admin/books.json", {
        params: {
          search: searchText,
          status: searchStatus,
          sort_direction,
          sort_field,
          page
        }
      })
      if (page === 1) books = []
      loadMore = !(resp.data.books.length < limit)

      books = [...books, resp.data.books].flat()
    } catch (e) {
      console.error(e)
      toast("Error searching books", "error")
    } finally {
      searching = false
    }
  }

  let loadMore = $state(true)
  function loadMoreBooks() {
    if (!loadMore) return
    page++
    search(page)
  }

  let showBookModal = $state(false)

  function showBook(book: BookType) {
    currentBook = book
    showBookModal = true
  }
</script>

<Layout {...props}>
  <h1 slot="title">Books</h1>
  <div>
    <div class="flex gap-2 py-4">
      <div class="flex gap-1 input-cont">
        <input
          type="text"
          class="!w-80 !mt-0 input"
          placeholder="Title or author"
          bind:value={searchText}
        />
        <select bind:value={searchStatus} class="!w-36 !mt-0">
          <option value=""></option>
          {#each statuses as s}
            <option value={s}>{s}</option>
          {/each}
        </select>
        <button
          class="btn primary"
          onclick={() => search(1)}
          disabled={searching}
        >
          Search
        </button>
      </div>

      <div class="w-full text-right">
        <button
          class="btn primary font-bold text-lg"
          onclick={() => {
            currentBook = {}
            status = "new"
            openFormModal = true
          }}
        >
          + New Book
        </button>
      </div>
    </div>
  </div>

  <div class="bg-white rounded-lg">
    <table class="min-w-full divide-y divide-gray-300">
      <thead class="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
        <tr>
          <th
            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
          >
            No.
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <SortField
              field="id"
              current={sort_field}
              dir={sort_direction}
              on:sort={({ detail }) => sort(detail)}
            >
              ID
            </SortField>
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <SortField
              field="title"
              current={sort_field}
              dir={sort_direction}
              on:sort={({ detail }) => sort(detail)}
              label="Title"
            />
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <SortField
              field="author"
              current={sort_field}
              dir={sort_direction}
              on:sort={({ detail }) => sort(detail)}
              label="Author"
            />
          </th>
          <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >Categories / Values</th
          >
          <th></th>
        </tr>
      </thead>

      {#each books as book, i}
        <!-- svelte-ignore node_invalid_placement_ssr -->
        <tr class:bg-red-200={book._selected}>
          <td
            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >{i + 1}</td
          >
          <td class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >{book.id}</td
          >
          <td
            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            style="max-width:300px"
          >
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="gap-1 cursor-pointer hover:bg-blue-500 p-2"
              onclick={() => showBook(book)}
            >
              <p class="link">{book.title}</p>
              <img
                src={book.image_url}
                class="mt-4 w-26 h-auto"
                alt={book.title}
              />
            </div>
          </td>
          <td class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <div class="p-2 flex flex-col">
              <div class="font-bold text-lg" title="Author">
                {book.author}
              </div>
              <div class="mt-2">
                <span class="label-color">Status:</span>
                {book.status}
              </div>
              <div class="flex items-center mt-1">
                <span class="label-color">Public:</span>
                &nbsp;
                {#if book.public}
                  <Check color="green" />
                {:else}
                  <X color="red" />
                {/if}
              </div>
              <div class="font-semibold italic" title="Publication date">
                {formatDate(book.publication_date)}
              </div>
              <div>
                {#if book.affiliate_link}
                  <a href={book.affiliate_link} target="_blank"
                    >Affiliate Link</a
                  >
                {/if}
              </div>
              <div class="flex-1"></div>
            </div>
          </td>
          <td class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            <div>
              <span class="">Categories</span>
              {@html bullets(book.categories)}
            </div>
            <div>
              <span class="label-color">Values</span>
              {@html bullets(book.values)}
            </div>
          </td>
          <td class="px-2 py-3.5 text-gray-800">
            <div class="flex gap-x-4 items-center justify-center">
              <button onclick={() => editBook(book)}>
                <Pen />
              </button>
              <button onclick={() => deleteBook(book)}>
                <Trash />
              </button>
            </div>
          </td>
        </tr>
      {/each}
    </table>
  </div>

  <div class="text-center mt-4">
    {#if loadMore}
      <button disabled={searching} onclick={loadMoreBooks} class="btn primary">
        Load more
      </button>
    {/if}
  </div>

  <Modal bind:open={openFormModal} large={true}>
    <Form
      book={currentBook}
      onupdate={(book) => updateBook(book)}
      oncreate={(book) => addBook(book)}
      {status}
    />
  </Modal>

  <Modal bind:open={showBookModal} large={true}>
    <Book book={currentBook} />
  </Modal>
</Layout>
